import React from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { MediaInput, MediaInputValue } from '../../../common/media-input';
import { uploadMediaInputFiles } from '../../../../services/file-service';
import { AlertService } from '../../../../services/alert.service';
import { InputWrapper } from '../../../common/input-wrapper';
import { decimalValidation, required } from '../../../../utils/validations';
import useSWRMutation from 'swr/mutation';
import { trpc, RouterInput } from '../../../../api/trpc';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import {
  CleanCustomsDeliveryOrderDeliveryType,
  RbacObject,
} from '../../../../export-types/cleaned-types';
import { Select } from '../../../common/select.component';
import { AccessDenied } from '../../../common/AccessDenied';
import { Rbac } from '../../../common/Rbac';

type RequestEditFormProps = {
  id: string;
  defaultValues: {
    name: string;
    files?: MediaInputValue[];
    weight: number | null;
    volume: number | null;
    price: number | null;
    amount: number | null;
    comment: string | null;
    receiptCity: string | null;
    documentTypes: Array<string> | null;
    deliveryType: CleanCustomsDeliveryOrderDeliveryType | null;
    bitrixDealId: string | null;
  };
  onSave: () => void;
  onCancel: () => void;
};

export const CustomDeliveryRequestEditForm: React.FC<RequestEditFormProps> = (
  props,
) => {
  const intl = useIntl();
  const { trigger } = useSWRMutation(
    ['customsDelivery.updateOne'],
    (_k, { arg }: { arg: RouterInput['customsDelivery']['updateOne'] }) =>
      trpc.customsDelivery.updateOne.mutate(arg),
  );
  const { control, register, handleSubmit, formState } = useForm<{
    name: string;
    weight: number | null;
    volume: number | null;
    price: number | null;
    amount: number | null;
    documentTypes: { value: string }[];
    files: MediaInputValue[];
    link: string | null;
    comment: string | null;
    receiptCity: string | null;
    deliveryType: CleanCustomsDeliveryOrderDeliveryType | null;
    bitrixDealId: string | null;
  }>({
    defaultValues: {
      ...props.defaultValues,
      documentTypes: (props.defaultValues?.documentTypes || []).map((type) => ({
        value: type,
      })),
    },
  });
  const { fields, append } = useFieldArray({
    control,
    name: 'documentTypes',
  });

  const onSubmit = handleSubmit(async (form) => {
    const files = await uploadMediaInputFiles(form.files || []);
    await trigger({
      id: props.id,
      name: form.name.trim(),
      price: form.price ? +form.price : undefined,
      amount: form.amount ? +form.amount : undefined,
      weight: form.weight ? +form.weight : undefined,
      volume: form.volume ? +form.volume : undefined,
      receiptCity: form.receiptCity ?? undefined,
      deliveryType: form.deliveryType ?? undefined,
      documentTypes: form.documentTypes
        .map((type) => type.value)
        .filter((value) => !!value),
      files: files.map((file) => ({ id: file.file.id })),
      comment: form.comment ?? '',
      bitrixDealId: form.bitrixDealId ?? undefined,
    });
    AlertService.success();
    props.onSave();
  });

  function handleCancel() {
    props.onCancel();
  }

  return (
    <form onSubmit={onSubmit}>
      <div className="d-flex flex-row justify-content-between">
        <div className="fs-18 fw-600 color-gray-450">О заказе</div>
        <div>
          <button
            className="btn btn-link text-decoration-none"
            disabled={formState.isSubmitting}
          >
            {formState.isSubmitting ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              'Сохранить'
            )}
          </button>
          <button
            className="btn btn-link text-decoration-none"
            type="button"
            disabled={formState.isSubmitting}
            onClick={handleCancel}
          >
            Отменить
          </button>
        </div>
      </div>

      <InputWrapper
        theme="light"
        className="mt-3"
        title="Название"
        error={formState.errors.name?.message}
      >
        <input
          type="text"
          placeholder="Название"
          className="form-control"
          {...register('name', { ...required })}
        />
      </InputWrapper>

      <Controller
        control={control}
        name="files"
        render={({ field, fieldState }) => (
          <InputWrapper
            theme="light"
            className="mt-3"
            title="Файлы"
            error={fieldState.error?.message}
          >
            <MediaInput
              {...field}
              className="mt-2"
              multiple
              fileTypes={[
                'application/msword',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'image/png',
                'image/jpeg',
                'video/*',
                'application/pdf',
              ]}
            />
          </InputWrapper>
        )}
      />

      <Controller
        control={control}
        name={`amount`}
        rules={{
          required: intl.formatMessage({
            defaultMessage: 'Укажите количество штук',
            id: 'CustomsDeliveryItemForm.input-label.specifyAmount',
            description: 'Пояснение к полю ввода',
          }),
        }}
        render={({ field, fieldState }) => (
          <InputWrapper
            required
            title={intl.formatMessage({
              defaultMessage: 'Количество штук',
              id: 'CustomsDeliveryItemForm.input-label.amount',
              description: 'Заголовок поля ввода',
            })}
            className="mb-4 text-base-lh-100"
          >
            <div className="relative mb-2 rounded d-flex gap-2">
              <input
                defaultValue={field.value || undefined}
                onChange={field.onChange}
                type="number"
                min={1}
                step={1}
                autoComplete="off"
                className={classNames(
                  fieldState.error ? 'border-red-500' : 'border-gray-300',
                  'block w-full rounded border  text-left text-base-lh-125 text-gray-800 form-control',
                )}
              />
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center py-1 pr-3">
                <span className="text-base-lh-125 text-gray-400">шт</span>
              </div>
            </div>
          </InputWrapper>
        )}
      />

      <Controller
        control={control}
        rules={{
          required: intl.formatMessage({
            defaultMessage: 'Укажите стоимость товаров',
            id: 'CustomsDeliveryItemForm.input-label.specifyTheCostOfGoods',
            description: 'Пояснение к полю ввода',
          }),
        }}
        name={`price`}
        render={({ field, fieldState }) => (
          <InputWrapper
            required
            error={fieldState.error?.message}
            title={intl.formatMessage({
              defaultMessage: 'Общая стоимость товаров',
              id: 'CustomsDeliveryItemForm.input-label.totalCostOfGoods',
              description: 'Заголовок поля ввода',
            })}
            className="mb-4 text-base-lh-100"
          >
            <div className="relative  rounded d-flex gap-2 h-auto">
              <input
                defaultValue={field.value || undefined}
                onChange={field.onChange}
                className={classNames(
                  fieldState.error ? 'border-red-500' : 'border-gray-300',
                  'block w-full rounded border text-left text-base-lh-125 text-gray-800 form-control ',
                )}
                aria-describedby="price-currency"
                inputMode="decimal"
                autoComplete="off"
                placeholder="0"
                type="text"
                data-testid="input-totalCost"
              />
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center py-1 pr-3">
                <span className="text-base-lh-125 text-gray-400">¥</span>
              </div>
            </div>
          </InputWrapper>
        )}
      />

      <InputWrapper
        theme="light"
        className="mt-3"
        title="Вес"
        error={formState.errors.volume?.message}
      >
        <div className="input-group">
          <input
            className="form-control"
            type="number"
            min={0}
            step={0.01}
            {...register('weight', { ...required, ...decimalValidation })}
          />
          <span className="input-group-text">кг</span>
        </div>
      </InputWrapper>
      <InputWrapper
        theme="light"
        className="mt-3"
        title="Объём"
        error={formState.errors.volume?.message}
      >
        <div className="input-group">
          <input
            className="form-control"
            type="number"
            min={0}
            step={0.01}
            {...register('volume', { ...required, ...decimalValidation })}
          />
          <span className="input-group-text">м³</span>
        </div>
      </InputWrapper>
      <InputWrapper
        theme="light"
        className="mt-3"
        title="Комментарий"
        error={formState.errors.volume?.message}
      >
        <div className="input-group">
          <textarea className="form-control" {...register('comment')} />
        </div>
      </InputWrapper>
      <Rbac
        object={RbacObject.CustomsDelivery}
        action={'write:declarant'}
        otherwise={() => <AccessDenied />}
      >
        <InputWrapper
          theme="light"
          className="mt-3"
          title="Вид разрешительных документов"
          error={formState.errors.volume?.message}
        >
          <div className={'d-flex gap-2 flex-column'}>
            {fields.map((_, index) => (
              <Controller
                name={`documentTypes.${index}`}
                control={control}
                render={({ field }) => {
                  return (
                    <input
                      className="form-control"
                      type="text"
                      value={field.value.value || ''}
                      onChange={(event) => {
                        field.onChange({ value: event.target.value });
                      }}
                    />
                  );
                }}
              />
            ))}
            <button
              type="button"
              className="btn btn-dashed w-100 d-flex  flex-row justify-content-center p-1"
              onClick={() => {
                append({ value: '' });
              }}
            >
              <i className="bi bi-plus-circle me-2" />
            </button>
          </div>
        </InputWrapper>{' '}
      </Rbac>

      <Controller
        name="deliveryType"
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            options={[
              { label: 'Море', value: 'sea' },
              { label: 'Авто', value: 'auto' },
              {
                label: 'Ж/Д',
                value: 'railway',
              },
            ]}
            title="Способ доставки"
            placeholder="Способ доставки"
            error={formState.errors.deliveryType?.message}
          />
        )}
      />

      <InputWrapper
        theme="light"
        className="mt-3"
        title="Город получения"
        error={formState.errors.receiptCity?.message}
      >
        <div className="input-group">
          <input
            className="form-control"
            type="text"
            {...register('receiptCity', { ...required })}
          />
        </div>
      </InputWrapper>

      <Controller
        control={control}
        name={`bitrixDealId`}
        render={({ field, fieldState }) => (
          <InputWrapper
            title={intl.formatMessage({
              defaultMessage: 'Сделка Битрикс24',
              id: 'CustomsDeliveryItemForm.input-label.bitrixDealId',
              description: 'Заголовок поля ввода',
            })}
            className="mb-4 text-base-lh-100"
          >
            <div className="relative mb-2 rounded d-flex gap-2">
              <input
                defaultValue={field.value || undefined}
                onChange={field.onChange}
                type="number"
                min={1}
                step={1}
                autoComplete="off"
                className={classNames(
                  fieldState.error ? 'border-red-500' : 'border-gray-300',
                  'block w-full rounded border  text-left text-base-lh-125 text-gray-800 form-control',
                )}
              />
            </div>
          </InputWrapper>
        )}
      />
    </form>
  );
};
